import { useEffect, useState, useRef } from 'react';
import websocketService, { ConnectionState } from '../services/websocket';
import apiService, { ServiceType } from '../services/api';
import type { PriceResponseDto } from '../api-schema/generated/types';

const coreService = apiService.getService(ServiceType.CORE);

/**
 * Enrich PriceResponseDto with flat bid/ask fields parsed from hidden_system_status
 * when the flat fields are missing. The websocket price_update may only include
 * hidden_system_status (JSON string with bid/offer arrays) without the flat
 * bid_price_X / ask_price_X fields that the BidAsk component expects.
 */
function enrichBidAskFromStatus(data: PriceResponseDto): PriceResponseDto {
  if (data.bid_price_1 != null && data.ask_price_1 != null) return data;
  if (!data.hidden_system_status) return data;

  try {
    const status = JSON.parse(data.hidden_system_status);
    const enriched: any = { ...data };

    if (Array.isArray(status.bid)) {
      status.bid.forEach((level: { price: number; qtty: number }, i: number) => {
        if (i < 10) {
          enriched[`bid_price_${i + 1}`] = level.price;
          enriched[`bid_quantity_${i + 1}`] = Math.round(level.qtty * 10);
        }
      });
    }

    if (Array.isArray(status.offer)) {
      status.offer.forEach((level: { price: number; qtty: number }, i: number) => {
        if (i < 10) {
          enriched[`ask_price_${i + 1}`] = level.price;
          enriched[`ask_quantity_${i + 1}`] = Math.round(level.qtty * 10);
        }
      });
    }

    return enriched as PriceResponseDto;
  } catch {
    return data;
  }
}

interface UseWebSocketPriceDataOptions {
  symbol: string | null | undefined;
  enabled?: boolean;
}

export const useWebSocketPriceData = ({ symbol, enabled = true }: UseWebSocketPriceDataOptions) => {
  const [priceData, setPriceData] = useState<PriceResponseDto | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const initialFetchDone = useRef(false);

  useEffect(() => {
    if (!symbol || !enabled) {
      setPriceData(null);
      setIsConnected(false);
      initialFetchDone.current = false;
      return;
    }

    let cancelled = false;
    initialFetchDone.current = false;

    const fetchInitialPrice = async () => {
      try {
        const response = await coreService.get(`/price/latest/${symbol}`, { timeout: 5000 });
        if (!cancelled && response.data) {
          setPriceData(enrichBidAskFromStatus(response.data));
        }
      } catch (err) {
        console.error(`Failed to fetch initial price for ${symbol}:`, err);
      } finally {
        initialFetchDone.current = true;
      }
    };

    fetchInitialPrice();

    websocketService.connect();

    const unsubscribe = websocketService.subscribe(symbol, (data: PriceResponseDto) => {
      if (!cancelled) {
        setPriceData(enrichBidAskFromStatus(data));
      }
    });

    const unsubscribeState = websocketService.onConnectionStateChange((state) => {
      if (!cancelled) {
        setIsConnected(state === ConnectionState.CONNECTED);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeState();
      setPriceData(null);
      setIsConnected(false);
    };
  }, [symbol, enabled]);

  return { priceData, isConnected };
};
